/* eslint-disable */
import { useEffect, useMemo, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import api from "../lib/api";

/**
 * Region-scoped beta gate.
 *
 * Two separate beta tracks:
 *   - DMV:          /dmv  (password: `Metierbeta2026dmv`, storage: metier_beta_dmv_unlocked)
 *   - New England:  /nh, /ma (password: `Metierbeta2026NE`, storage: metier_beta_ne_unlocked)
 *
 * Any shared page (workspaces, spaces, community, signup, profile, admin, etc.)
 * unlocks when EITHER regional password has been entered, that's the "loose"
 * mode confirmed with the CEO.
 *
 * Fully public marketing surfaces (hours, about, partner analytics, llm-info)
 * bypass the gate entirely.
 */

// Persistent unlock flags, one per beta track.
const UNLOCK_KEYS = {
  dmv: "metier_beta_dmv_unlocked",
  ne:  "metier_beta_ne_unlocked",
  any: "metier_beta_unlocked",  // legacy, treated as universal unlock
};

const REGION_META = {
  dmv: {
    label: "DMV",
    tagline: "Washington DC · Maryland · Virginia",
    otherLabel: "New Hampshire",
    otherPath: "/nh",
  },
  ne: {
    label: "New Hampshire",
    tagline: "New Hampshire · Massachusetts",
    otherLabel: "DMV",
    otherPath: "/dmv",
  },
};

// Fully public, no gate at all.
const PUBLIC_PREFIXES = [
  "/hours",
  "/about",
  "/support",
  "/contact",
  "/llm-info",
  "/login",
  "/signup",
  "/auth/callback",
  "/partner", // public partner analytics
];

// Region-scoped routes.
function regionForPath(pathname) {
  if (pathname === "/dmv" || pathname.startsWith("/dmv/")) return "dmv";
  if (pathname === "/nh" || pathname.startsWith("/nh/")) return "ne";
  if (pathname === "/ma" || pathname.startsWith("/ma/")) return "ne";
  if (pathname.startsWith("/regions/dmv")) return "dmv";
  if (pathname.startsWith("/regions/nh") || pathname.startsWith("/regions/ma") || pathname.startsWith("/regions/ne")) return "ne";
  return null;
}

function isPublic(pathname) {
  return PUBLIC_PREFIXES.some(p => pathname === p || pathname.startsWith(p + "/"));
}

function readUnlocks() {
  try {
    return {
      dmv: localStorage.getItem(UNLOCK_KEYS.dmv) === "1",
      ne:  localStorage.getItem(UNLOCK_KEYS.ne)  === "1",
      any: localStorage.getItem(UNLOCK_KEYS.any) === "1",
    };
  } catch {
    return { dmv: false, ne: false, any: false };
  }
}

// Feature flag, flip this back to true (or wire to REACT_APP_BETA_GATE_ENABLED)
// to bring the invitation gate back. Blanked in Session 15 to open the network.
const BETA_GATE_ENABLED = process.env.REACT_APP_BETA_GATE_ENABLED === "1";

export default function BetaGate({ children }) {
  if (!BETA_GATE_ENABLED) return children;
  // eslint-disable-next-line no-unreachable
  const location = useLocation();
  const [status, setStatus] = useState(null);   // { gate_active, gates: {dmv, ne, any} }
  const [unlocks, setUnlocks] = useState(readUnlocks);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/beta/status");
        if (!cancelled) setStatus(r.data);
      } catch {
        if (!cancelled) setStatus({ gate_active: false, gates: {} });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Which gate does this path need?
  const requiredRegion = useMemo(() => regionForPath(location.pathname), [location.pathname]);
  const isRegionScoped = requiredRegion !== null;
  const isPublicSurface = isPublic(location.pathname);
  const anyUnlocked = unlocks.dmv || unlocks.ne || unlocks.any;

  const shouldPrompt = (() => {
    if (!status) return false;
    if (!status.gate_active) return false;
    if (isPublicSurface) return false;
    if (isRegionScoped) {
      // Regional page, must have that specific region unlocked.
      const activeForRegion = status.gates?.[requiredRegion] ?? status.gates?.any;
      if (!activeForRegion) return false;
      return !unlocks[requiredRegion];
    }
    // Shared/authenticated page, any regional unlock is fine.
    return !anyUnlocked;
  })();

  const promptRegion = requiredRegion || "ne"; // default hero copy when shared page needs unlocking

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const r = await api.post("/beta/gate", { password, region: promptRegion });
      const unlockedRegion = r.data?.region || promptRegion;
      const key = UNLOCK_KEYS[unlockedRegion] || UNLOCK_KEYS.any;
      try { localStorage.setItem(key, "1"); } catch {}
      setUnlocks(readUnlocks());
    } catch (err) {
      setError(err?.response?.data?.detail || "Invalid beta password");
    } finally {
      setBusy(false);
    }
  };

  // Don't flicker while resolving.
  if (status === null) return null;
  if (!shouldPrompt) return children;

  const meta = REGION_META[promptRegion];

  return (
    <div className="beta-gate" data-testid={`beta-gate-${promptRegion}`}>
      <div className="beta-gate-card">
        <span className="beta-gate-eyebrow">Métier · {meta.label} beta</span>
        <h1 className="beta-gate-h1">By <em>invitation</em>.</h1>
        <p className="beta-gate-lede">
          The {meta.label} activation ({meta.tagline}) is in private beta. Enter your invitation password to continue.
          {" "}
          {isRegionScoped ? (
            <>Coming from {meta.otherLabel}? <Link to={meta.otherPath} className="beta-gate-link" data-testid="beta-gate-other-region">Visit the {meta.otherLabel} activation →</Link></>
          ) : (
            <>You&apos;ll need an invitation password from a Métier activation to continue.</>
          )}
        </p>
        <form onSubmit={onSubmit} className="beta-gate-form">
          <label className="beta-gate-field">
            <span>{meta.label} invitation password</span>
            <input
              type="password"
              autoFocus
              autoComplete="off"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="beta-gate-input"
            />
          </label>
          {error && <div className="beta-gate-error" data-testid="beta-gate-error">{error}</div>}
          <button type="submit" className="m-btn-gold" disabled={busy} data-testid="beta-gate-submit">
            {busy ? "Checking…" : "Enter"}
          </button>
        </form>
        <div className="beta-gate-footer">
          <Link to="/nh" data-testid="beta-gate-ne">New Hampshire activation</Link>
          <Link to="/hours" data-testid="beta-gate-hours">Métier Hours</Link>
          <Link to="/about" data-testid="beta-gate-about">About</Link>
        </div>
      </div>
    </div>
  );
}
